import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Notiflix from 'notiflix';
import { 
  getOrganizationOverview,
  addUserToOrganization,
  removeUserFromOrganization,
  addProjectToOrganization,
  removeProjectFromOrganization,
  getOrganizationRequestsBySlug,
  acceptOrganizationRequest,
  rejectOrganizationRequest,
  getOrganizationTypeLabel,
  getUserStatusLabel,
  getUserStatusColor,
  getProjectStatusLabel,
  getProjectStatusColor
} from '../services/organizations';
import { 
  FiArrowLeft, 
  FiUsers, 
  FiFolder, 
  FiTrendingUp, 
  FiCalendar,
  FiFileText,
  FiTarget,
  FiPlus,
  FiTrash2,
  FiCheck,
  FiX,
  FiEdit2,
  FiMoreVertical,
  FiBell,
  FiClock,
  FiBarChart3
} from 'react-icons/fi';
import { 
  Button, 
  Card, 
  CardBody, 
  Chip, 
  Avatar, 
  Dropdown, 
  DropdownTrigger, 
  DropdownMenu, 
  DropdownItem,
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Input,
  Select,
  SelectItem,
  Table,
  TableHeader,
  TableColumn,
  TableBody,
  TableRow,
  TableCell,
  Tabs,
  Tab
} from '@nextui-org/react';
import OrganizationFinancials from '../components/Organization/OrganizationFinancials';
import { getProjectFinancialSummary } from '../services/financials';
import { postProjectsUserRole } from '../services/projects';
// AuthProvider bulunmuyorsa localStorage üzerinden kullanıcı okunacak

// Kullanıcı yetki kontrol fonksiyonu
function hasOrgAdminAccess(projects, currentUser) {
  if (!currentUser) return false;
  // Global admin
  if (currentUser.urole === 11) return true;
  // Organizasyondaki herhangi bir projede OWNER(2) veya MODERATOR(3) ise
  const myUid = +currentUser.uid || +currentUser.id;
  for (const p of projects ?? []) {
    if (Array.isArray(p.users)) {
      for (const u of p.users) {
        // Esnek id anahtarları
        const puid = +u.puid || +u.uid || +u.id;
        if (puid === myUid && (u.pstatus === 2 || u.pstatus === 3)) {
          return true;
        }
      }
    }
  }
  return false;
}

const OrganizationDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [organization, setOrganization] = useState(null);
  const [projects, setProjects] = useState([]);
  const [users, setUsers] = useState([]);
  const [statistics, setStatistics] = useState({});
  const [requests, setRequests] = useState([]);
  const [reminders, setReminders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [isAddUserModalOpen, setIsAddUserModalOpen] = useState(false);
  const [isAddProjectModalOpen, setIsAddProjectModalOpen] = useState(false);
  const [newUserId, setNewUserId] = useState('');
  const [newProjectId, setNewProjectId] = useState('');
  const [financialOverview, setFinancialOverview] = useState({ income: 0, expense: 0, asset: 0, balance: 0 });
  const [financialLoading, setFinancialLoading] = useState(false);
  // Rol değiştirme modal state
  const [isRoleModalOpen, setIsRoleModalOpen] = useState(false);
  const [roleTargetUser, setRoleTargetUser] = useState(null);
  const [selectedProjectSlug, setSelectedProjectSlug] = useState('');
  const [selectedRole, setSelectedRole] = useState('1'); // 2 OWNER, 3 MODERATOR, 1 MEMBER
  // Kullanıcıyı context veya localstorage'dan al
  const getCurrentUserFromStorage = () => {
    try {
      const keys = ['user', 'currentUser', 'authUser', 'me'];
      for (const key of keys) {
        const raw = localStorage.getItem(key);
        if (raw) {
          const parsed = JSON.parse(raw);
          if (parsed && (parsed.id || parsed.uid || parsed.urole)) return parsed;
        }
      }
    } catch (_) {}
    return null;
  };
  const currentUser = getCurrentUserFromStorage();
  const [accessDenied, setAccessDenied] = useState(false);

  useEffect(() => {
    if (slug) {
      fetchOrganizationData();
      fetchRequests();
    }
  }, [slug]);

  useEffect(() => {
    // Projeler geldiğinde finansal özetleri hesapla
    if (projects && projects.length > 0) {
      fetchFinancialOverview(projects);
    } else {
      setFinancialOverview({ income: 0, expense: 0, asset: 0, balance: 0 });
    }
  }, [projects]);

  const fetchOrganizationData = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getOrganizationOverview(slug);
      // API mesajı ile forbidden kontrolü
      if (data.message && data.message.includes('Yetkisiz')) {
        setError(data.message);
        setAccessDenied(true);
        return;
      }
      setOrganization(data.organization);
      setProjects(data.projects || []);
      setUsers(data.users || []);
      setStatistics(data.statistics || {});
      
      // Tüm projelerden hatırlatmaları topla
      const allReminders = [];
      data.projects?.forEach(project => {
        if (project.reminders?.latest) {
          project.reminders.latest.forEach(reminder => {
            allReminders.push({
              ...reminder,
              projectName: project.pname,
              projectSlug: project.pslug
            });
          });
        }
      });
      setReminders(allReminders);
    } catch (error) {
      console.error('Organizasyon verileri yüklenirken hata:', error);
      setError('Organizasyon verileri yüklenirken bir hata oluştu.');
    } finally {
      setLoading(false);
    }
  };

  const fetchFinancialOverview = async (projList) => {
    try {
      setFinancialLoading(true);
      const tasks = (projList || []).map((p) => {
        const slug = p.pslug || p.slug || p.project_slug;
        if (!slug) return Promise.resolve(null);
        return getProjectFinancialSummary({ slug }).catch(() => null);
      });
      const results = await Promise.all(tasks);
      const total = results.reduce((acc, s) => {
        if (!s) return acc;
        acc.income += s.total_income || 0;
        acc.expense += s.total_expense || 0;
        acc.asset += s.total_asset || 0;
        acc.balance += s.net_balance || 0;
        return acc;
      }, { income: 0, expense: 0, asset: 0, balance: 0 });
      setFinancialOverview(total);
    } catch (e) {
      // sessiz geç
    } finally {
      setFinancialLoading(false);
    }
  };

  const fetchRequests = async () => {
    try {
      const reqs = await getOrganizationRequestsBySlug(slug);
      setRequests(reqs);
    } catch (error) {
      console.error('İstekler yüklenirken hata:', error);
    }
  };

  const handleAcceptRequest = async (requestId) => {
    try {
      Notiflix.Loading.circle('İstek onaylanıyor...');
      await acceptOrganizationRequest(requestId);
      await fetchRequests();
      await fetchOrganizationData();
      Notiflix.Notify.success('Kullanıcı başarıyla onaylandı!');
    } catch (error) {
      console.error('İstek kabul edilirken hata:', error);
      Notiflix.Notify.failure(error.response?.data?.message || 'İstek kabul edilirken bir hata oluştu.');
    } finally {
      Notiflix.Loading.remove();
    }
  };

  const handleRejectRequest = async (requestId) => {
    try {
      Notiflix.Loading.circle('İstek reddediliyor...');
      await rejectOrganizationRequest(requestId);
      await fetchRequests();
      Notiflix.Notify.success('İstek reddedildi.');
    } catch (error) {
      console.error('İstek reddedilirken hata:', error);
      Notiflix.Notify.failure(error.response?.data?.message || 'İstek reddedilirken bir hata oluştu.');
    } finally {
      Notiflix.Loading.remove();
    }
  };

  const handleAddUser = async () => {
    if (!newUserId.trim()) {
      Notiflix.Notify.warning('Lütfen kullanıcı ID\'si girin.');
      return;
    }

    try {
      Notiflix.Loading.circle('Kullanıcı ekleniyor...');
      await addUserToOrganization(organization.id, parseInt(newUserId));
      await fetchOrganizationData();
      setIsAddUserModalOpen(false);
      setNewUserId('');
      Notiflix.Notify.success('Kullanıcı başarıyla eklendi!');
    } catch (error) {
      console.error('Kullanıcı eklenirken hata:', error);
      Notiflix.Notify.failure(error.response?.data?.message || 'Kullanıcı eklenirken bir hata oluştu.');
    } finally {
      Notiflix.Loading.remove();
    }
  };

  const handleRemoveUser = async (userId, userName) => {
    Notiflix.Confirm.show(
      'Kullanıcıyı Çıkar',
      `${userName} kullanıcısını organizasyondan çıkarmak istediğinizden emin misiniz?`,
      'Evet, Çıkar',
      'İptal',
      async () => {
        try {
          Notiflix.Loading.circle('Kullanıcı çıkarılıyor...');
          await removeUserFromOrganization(organization.id, userId);
          await fetchOrganizationData();
          Notiflix.Notify.success('Kullanıcı başarıyla çıkarıldı!');
        } catch (error) {
          console.error('Kullanıcı çıkarılırken hata:', error);
          Notiflix.Notify.failure(error.response?.data?.message || 'Kullanıcı çıkarılırken bir hata oluştu.');
        } finally {
          Notiflix.Loading.remove();
        }
      },
      () => {}
    );
  };

  // Kullanıcı rolü değiştir
  const openRoleModal = (user) => {
    setRoleTargetUser(user);
    // varsayılan proje slug'ı: ilk proje
    const defaultSlug = (projects?.[0]?.pslug) || '';
    setSelectedProjectSlug(defaultSlug);
    setSelectedRole('1');
    setIsRoleModalOpen(true);
  };

  const handleChangeUserRole = async () => {
    if (!selectedProjectSlug || !roleTargetUser) {
      Notiflix.Notify.warning('Proje ve rol seçmelisiniz');
      return;
    }
    try {
      Notiflix.Loading.pulse('Rol güncelleniyor...');
      await postProjectsUserRole({ slug: selectedProjectSlug, uid: roleTargetUser.id, role: parseInt(selectedRole) });
      setIsRoleModalOpen(false);
      await fetchOrganizationData();
      Notiflix.Notify.success('Kullanıcı rolü güncellendi');
    } catch (error) {
      const msg = error?.response?.data?.message || 'Rol güncelleme başarısız';
      Notiflix.Notify.failure(msg);
    } finally {
      Notiflix.Loading.remove();
    }
  };

  const handleAddProject = async () => {
    if (!newProjectId.trim()) {
      Notiflix.Notify.warning('Lütfen proje ID\'si girin.');
      return;
    }

    try {
      Notiflix.Loading.circle('Proje ekleniyor...');
      await addProjectToOrganization(organization.id, parseInt(newProjectId));
      await fetchOrganizationData();
      setIsAddProjectModalOpen(false);
      setNewProjectId('');
      Notiflix.Notify.success('Proje başarıyla eklendi!');
    } catch (error) {
      console.error('Proje eklenirken hata:', error);
      Notiflix.Notify.failure(error.response?.data?.message || 'Proje eklenirken bir hata oluştu.');
    } finally {
      Notiflix.Loading.remove();
    }
  };

  const handleRemoveProject = async (projectId, projectName) => {
    Notiflix.Confirm.show(
      'Projeyi Çıkar',
      `${projectName} projesini organizasyondan çıkarmak istediğinizden emin misiniz?`,
      'Evet, Çıkar',
      'İptal',
      async () => {
        try {
          Notiflix.Loading.circle('Proje çıkarılıyor...');
          await removeProjectFromOrganization(organization.id, projectId);
          await fetchOrganizationData();
          Notiflix.Notify.success('Proje başarıyla çıkarıldı!');
        } catch (error) {
          console.error('Proje çıkarılırken hata:', error);
          Notiflix.Notify.failure(error.response?.data?.message || 'Proje çıkarılırken bir hata oluştu.');
        } finally {
          Notiflix.Loading.remove();
        }
      },
      () => {}
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-dark-200 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#da402f] dark:border-[#7375E1] mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Organizasyon yükleniyor...</p>
        </div>
      </div>
    );
  }

  if (error && (error.includes('Yetkisiz') || accessDenied)) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-red-600 mb-4">Erişim Yok</h2>
          <p className="text-gray-500 mb-6">
            Bu organizasyonu yalnızca proje sahibi, moderatör veya admin görebilir/düzenleyebilir.
          </p>
          <Button onClick={() => navigate('/organizations')}>
            Organizasyonlara Dön
          </Button>
        </div>
      </div>
    );
  }

  // Yönetim aksiyonlarını yalnızca yetkililere göster:
  const adminAccess = hasOrgAdminAccess(projects, currentUser);

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-dark-200 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center">
            <Button
              variant="light"
              onClick={() => navigate('/organizations')}
              className="mr-4"
              startContent={<FiArrowLeft size={16} />}
            >
              Geri
            </Button>
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
                {organization.title}
              </h1>
              <p className="text-gray-600 dark:text-gray-400">
                {organization.description}
              </p>
            </div>
          </div>
          <Chip 
            size="lg" 
            variant="flat"
            className="bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300"
          >
            {getOrganizationTypeLabel(organization.type)}
          </Chip>
        </div>

        {/* Tabs */}
        <div className="mb-6">
          <Tabs 
            selectedKey={activeTab} 
            onSelectionChange={setActiveTab}
            className="w-full"
            classNames={{
              tabList: "bg-white dark:bg-dark-300 border border-gray-200 dark:border-gray-700",
              tab: "data-[selected=true]:bg-red-50 dark:data-[selected=true]:bg-red-900/20 data-[selected=true]:text-red-600 dark:data-[selected=true]:text-red-400",
              cursor: "bg-red-100 dark:bg-red-900/30"
            }}
          >
            <Tab key="overview" title="Genel Bakış" />
            <Tab key="projects" title="Projeler" />
            <Tab key="users" title="Kullanıcılar" />
            <Tab key="reminders" title="Hatırlatmalar" />
            <Tab key="financials" title="Finans" />
          </Tabs>
        </div>

        {/* Tab Content */}
        {activeTab === 'overview' && (
          <>
            {/* Statistics Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
              <Card className="bg-white dark:bg-dark-300 border border-gray-200 dark:border-gray-700 shadow-none">
                <CardBody className="p-4">
                  <div className="flex items-center">
                    <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg mr-3">
                      <FiFolder className="text-blue-600 dark:text-blue-400" size={20} />
                    </div>
                    <div>
                      <p className="text-xs text-gray-600 dark:text-gray-400">Projeler</p>
                      <p className="text-xl font-bold text-gray-900 dark:text-white">
                        {statistics.project_count || 0}
                      </p>
                    </div>
                  </div>
                </CardBody>
              </Card>

              <Card className="bg-white dark:bg-dark-300 border border-gray-200 dark:border-gray-700 shadow-none">
                <CardBody className="p-4">
                  <div className="flex items-center">
                    <div className="p-2 bg-green-100 dark:bg-green-900/30 rounded-lg mr-3">
                      <FiUsers className="text-green-600 dark:text-green-400" size={20} />
                    </div>
                    <div>
                      <p className="text-xs text-gray-600 dark:text-gray-400">Kullanıcılar</p>
                      <p className="text-xl font-bold text-gray-900 dark:text-white">
                        {users.length}
                      </p>
                    </div>
                  </div>
                </CardBody>
              </Card>

              <Card className="bg-white dark:bg-dark-300 border border-gray-200 dark:border-gray-700 shadow-none">
                <CardBody className="p-4">
                  <div className="flex items-center">
                    <div className="p-2 bg-orange-100 dark:bg-orange-900/30 rounded-lg mr-3">
                      <FiTarget className="text-orange-600 dark:text-orange-400" size={20} />
                    </div>
                    <div>
                      <p className="text-xs text-gray-600 dark:text-gray-400">Toplam Görev</p>
                      <p className="text-xl font-bold text-gray-900 dark:text-white">
                        {statistics.total_tasks || 0}
                      </p>
                    </div>
                  </div>
                </CardBody>
              </Card>

              <Card className="bg-white dark:bg-dark-300 border border-gray-200 dark:border-gray-700 shadow-none">
                <CardBody className="p-4">
                  <div className="flex items-center">
                    <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg mr-3">
                      <FiTrendingUp className="text-purple-600 dark:text-purple-400" size={20} />
                    </div>
                    <div>
                      <p className="text-xs text-gray-600 dark:text-gray-400">Tamamlanma</p>
                      <p className="text-xl font-bold text-gray-900 dark:text-white">
                        {statistics.completion_rate || 0}%
                      </p>
                    </div>
                  </div>
                </CardBody>
              </Card>
            </div>

            

            {/* Grafiksel İstatistikler */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
              {/* Proje Durumu Grafiği */}
              <Card className="bg-white dark:bg-dark-300 border border-gray-200 dark:border-gray-700 shadow-none">
                <CardBody className="p-4">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                    Proje Durumu
                  </h3>
                  <div className="space-y-3">
                    {projects.map((project) => (
                      <div key={project.project_id} className="flex items-center justify-between">
                        <div className="flex-1">
                          <p className="text-sm font-medium text-gray-900 dark:text-white">
                            {project.pname}
                          </p>
                          <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2 mt-1">
                            <div 
                              className="bg-gradient-to-r from-red-500 to-red-600 h-2 rounded-full transition-all duration-300"
                              style={{ width: `${project.completion_rate || 0}%` }}
                            ></div>
                          </div>
                        </div>
                        <div className="ml-4 text-right">
                          <p className="text-sm font-semibold text-gray-900 dark:text-white">
                            {project.completion_rate || 0}%
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            {project.completed_tasks}/{project.total_tasks}
                          </p>
                        </div>
                      </div>
                    ))}
                    {projects.length === 0 && (
                      <div className="text-center py-8">
                        <div className="p-3 bg-gray-100 dark:bg-gray-800 rounded-full w-12 h-12 mx-auto mb-3 flex items-center justify-center">
                          <FiFolder className="text-gray-400" size={20} />
                        </div>
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          Henüz proje yok
                        </p>
                      </div>
                    )}
                  </div>
                </CardBody>
              </Card>

              {/* Kullanıcı Performansı */}
              <Card className="bg-white dark:bg-dark-300 border border-gray-200 dark:border-gray-700 shadow-none">
                <CardBody className="p-4">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                    Kullanıcı Performansı
                  </h3>
                  <div className="space-y-3">
                    {users.slice(0, 5).map((user) => (
                      <div key={user.id} className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <Avatar
                            src={user.uimg}
                            name={`${user.ufname} ${user.ulname}`}
                            size="sm"
                          />
                          <div>
                            <p className="text-sm font-medium text-gray-900 dark:text-white">
                              {user.ufname} {user.ulname}
                            </p>
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                              {user.total_tasks} görev
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center space-x-2">
                          <div className="w-16 bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                            <div 
                              className="bg-gradient-to-r from-green-500 to-green-600 h-2 rounded-full transition-all duration-300"
                              style={{ width: `${user.completion_rate || 0}%` }}
                            ></div>
                          </div>
                          <span className="text-sm font-semibold text-gray-900 dark:text-white w-10 text-right">
                            {user.completion_rate || 0}%
                          </span>
                        </div>
                      </div>
                    ))}
                    {users.length === 0 && (
                      <div className="text-center py-8">
                        <div className="p-3 bg-gray-100 dark:bg-gray-800 rounded-full w-12 h-12 mx-auto mb-3 flex items-center justify-center">
                          <FiUsers className="text-gray-400" size={20} />
                        </div>
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          Henüz kullanıcı yok
                        </p>
                      </div>
                    )}
                  </div>
                </CardBody>
              </Card>

              {/* Finansal Durum (Kompakt Grafik) */}
              <Card className="bg-white dark:bg-dark-300 border border-gray-200 dark:border-gray-700 shadow-none">
                <CardBody className="p-4">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                    Finansal Durum
                  </h3>
                  {(() => {
                    const income = financialOverview.income || 0
                    const expense = financialOverview.expense || 0
                    const asset = financialOverview.asset || 0
                    const balance = financialOverview.balance || 0
                    const totalFlow = Math.max(income + expense, 1)
                    const incomePct = Math.min(100, Math.round((income / totalFlow) * 100))
                    const expensePct = 100 - incomePct
                    const ringStyle = {
                      background: `conic-gradient(#16a34a ${incomePct}%, #dc2626 0)`
                    }
                    const fmt = (v) => `₺${v.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}`
                    const positive = balance >= 0
                    // Animasyon ve glow için ek style
                    const glowClass = positive ?
                      'shadow-[0_0_20px_2px_rgba(16,163,74,0.25)] animate-pulse' :
                      'shadow-[0_0_20px_2px_rgba(220,38,38,0.25)] animate-pulse'

                    return (
                      <div className="flex items-center gap-6">
                        {/* Donut + iç metin */}
                        <div className={"relative w-24 h-24 rounded-full flex items-center justify-center " + glowClass} style={ringStyle}>
                          <div className="absolute inset-2 bg-white dark:bg-dark-300 rounded-full flex flex-col items-center justify-center shadow-inner z-10">
                            <div className={`text-lg font-bold leading-none ${positive ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}
                                 style={{ letterSpacing: '-1px', lineHeight: '1.0' }}>
                              {positive ? '+' : '-'}
                              {Math.abs(balance).toLocaleString('tr-TR', { minimumFractionDigits: 0 })}
                              <span className="text-base align-top font-semibold"> ₺</span>
                            </div>
                            <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mt-1 text-center opacity-85">Net
                            </div>
                          </div>
                        </div>
                        {/* Legend + bars */}
                        <div className="flex-1 space-y-3">
                          <div className="flex items-center justify-between text-xs text-gray-600 dark:text-gray-400">
                            <div className="flex items-center gap-2">
                              <span className="inline-block w-2.5 h-2.5 rounded-full bg-green-500"></span>
                              <span>Gelir</span>
                            </div>
                            <span className="font-medium text-gray-900 dark:text-gray-100">{fmt(income)}</span>
                          </div>
                          <div className="w-full h-2 rounded-full bg-gradient-to-r from-green-100 to-green-200 dark:from-green-900/20 dark:to-green-900/10 overflow-hidden">
                            <div className="h-full bg-gradient-to-r from-green-500 to-emerald-500 rounded-full" style={{ width: `${Math.min(100, Math.round((income / Math.max(income, expense, 1)) * 100))}%` }}></div>
                          </div>

                          <div className="flex items-center justify-between text-xs text-gray-600 dark:text-gray-400">
                            <div className="flex items-center gap-2">
                              <span className="inline-block w-2.5 h-2.5 rounded-full bg-red-500"></span>
                              <span>Gider</span>
                            </div>
                            <span className="font-medium text-gray-900 dark:text-gray-100">{fmt(expense)}</span>
                          </div>
                          <div className="w-full h-2 rounded-full bg-gradient-to-r from-red-100 to-red-200 dark:from-red-900/20 dark:to-red-900/10 overflow-hidden">
                            <div className="h-full bg-gradient-to-r from-red-500 to-rose-500 rounded-full" style={{ width: `${Math.min(100, Math.round((expense / Math.max(income, expense, 1)) * 100))}%` }}></div>
                          </div>

                          <div className="flex items-center justify-between text-xs text-gray-600 dark:text-gray-400">
                            <div className="flex items-center gap-2">
                              <span className="inline-block w-2.5 h-2.5 rounded-full bg-blue-500"></span>
                              <span>Demirbaş</span>
                            </div>
                            <span className="font-medium text-gray-900 dark:text-gray-100">{fmt(asset)}</span>
                          </div>
                          <div className="w-full h-2 rounded-full bg-gradient-to-r from-blue-100 to-blue-200 dark:from-blue-900/20 dark:to-blue-900/10 overflow-hidden">
                            <div className="h-full bg-gradient-to-r from-blue-500 to-indigo-500 rounded-full" style={{ width: `${Math.min(100, Math.round((asset / Math.max(income, expense, asset, 1)) * 100))}%` }}></div>
                          </div>
                        </div>
                      </div>
                    )
                  })()}
                </CardBody>
              </Card>
            </div>

            {/* Genel İstatistikler */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              {/* Toplam Görev Dağılımı */}
              <Card className="bg-white dark:bg-dark-300 border border-gray-200 dark:border-gray-700 shadow-none">
                <CardBody className="p-4">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                    Görev Dağılımı
                  </h3>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                        <span className="text-sm text-gray-600 dark:text-gray-400">Tamamlanan</span>
                      </div>
                      <span className="text-sm font-semibold text-gray-900 dark:text-white">
                        {statistics.completed_tasks || 0}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <div className="w-3 h-3 bg-orange-500 rounded-full"></div>
                        <span className="text-sm text-gray-600 dark:text-gray-400">Devam Eden</span>
                      </div>
                      <span className="text-sm font-semibold text-gray-900 dark:text-white">
                        {statistics.ongoing_tasks || 0}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <div className="w-3 h-3 bg-gray-400 rounded-full"></div>
                        <span className="text-sm text-gray-600 dark:text-gray-400">Bekleyen</span>
                      </div>
                      <span className="text-sm font-semibold text-gray-900 dark:text-white">
                        {statistics.pending_tasks || 0}
                      </span>
                    </div>
                  </div>
                </CardBody>
              </Card>

              {/* Hatırlatma Özeti */}
              <Card className="bg-white dark:bg-dark-300 border border-gray-200 dark:border-gray-700 shadow-none">
                <CardBody className="p-4">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                    Hatırlatmalar
                  </h3>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <FiBell className="text-orange-500" size={16} />
                        <span className="text-sm text-gray-600 dark:text-gray-400">Toplam</span>
                      </div>
                      <span className="text-sm font-semibold text-gray-900 dark:text-white">
                        {reminders.length}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
                        <span className="text-sm text-gray-600 dark:text-gray-400">Domain</span>
                      </div>
                      <span className="text-sm font-semibold text-gray-900 dark:text-white">
                        {reminders.filter(r => r.type === 'domain').length}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <div className="w-3 h-3 bg-purple-500 rounded-full"></div>
                        <span className="text-sm text-gray-600 dark:text-gray-400">Hosting</span>
                      </div>
                      <span className="text-sm font-semibold text-gray-900 dark:text-white">
                        {reminders.filter(r => r.type === 'hosting').length}
                      </span>
                    </div>
                  </div>
                </CardBody>
              </Card>

              {/* Aktivite Özeti */}
              <Card className="bg-white dark:bg-dark-300 border border-gray-200 dark:border-gray-700 shadow-none">
                <CardBody className="p-4">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                    Aktivite Özeti
                  </h3>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <FiFolder className="text-blue-500" size={16} />
                        <span className="text-sm text-gray-600 dark:text-gray-400">Projeler</span>
                      </div>
                      <span className="text-sm font-semibold text-gray-900 dark:text-white">
                        {statistics.project_count || 0}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <FiUsers className="text-green-500" size={16} />
                        <span className="text-sm text-gray-600 dark:text-gray-400">Kullanıcılar</span>
                      </div>
                      <span className="text-sm font-semibold text-gray-900 dark:text-white">
                        {users.length}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <FiTarget className="text-orange-500" size={16} />
                        <span className="text-sm text-gray-600 dark:text-gray-400">Toplam Görev</span>
                      </div>
                      <span className="text-sm font-semibold text-gray-900 dark:text-white">
                        {statistics.total_tasks || 0}
                      </span>
                    </div>
                  </div>
                </CardBody>
              </Card>
            </div>

            {/* Bekleyen İstekler */}
            {requests.length > 0 && (
              <Card className="bg-white dark:bg-dark-300 border border-gray-200 dark:border-gray-700 shadow-none mb-6">
                <CardBody className="p-4">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                      Bekleyen İstekler
                    </h3>
                    <Chip color="warning" variant="flat" size="sm">
                      {requests.length}
                    </Chip>
                  </div>
                  <div className="space-y-3">
                    {requests.map((request) => (
                      <div key={request.request_id} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                        <div className="flex items-center">
                          <Avatar
                            src={request.user_img}
                            name={`${request.user_fname} ${request.user_lname}`}
                            size="sm"
                            className="mr-3"
                          />
                          <div>
                            <p className="font-medium text-gray-900 dark:text-white">
                              {request.user_fname} {request.user_lname}
                            </p>
                            <p className="text-sm text-gray-600 dark:text-gray-400">
                              Organizasyona katılmak istiyor • {new Date(request.request_date).toLocaleDateString('tr-TR')}
                            </p>
                          </div>
                        </div>
                        <div className="flex space-x-2">
                          <Button
                            size="sm"
                            color="success"
                            startContent={<FiCheck size={16} />}
                            onClick={() => handleAcceptRequest(request.request_id)}
                          >
                            Kabul Et
                          </Button>
                          <Button
                            size="sm"
                            color="danger"
                            startContent={<FiX size={16} />}
                            onClick={() => handleRejectRequest(request.request_id)}
                          >
                            Reddet
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardBody>
              </Card>
            )}
          
          </>
        )}

        {activeTab === 'financials' && (
          <Card className="bg-white dark:bg-dark-300 border border-gray-200 dark:border-gray-700 shadow-none">
            <CardBody className="p-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Finans
                </h3>
              </div>
              <OrganizationFinancials projects={projects} />
            </CardBody>
          </Card>
        )}

        {activeTab === 'projects' && (
          <Card className="bg-white dark:bg-dark-300 border border-gray-200 dark:border-gray-700 shadow-none">
            <CardBody className="p-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Projeler
                </h3>
                {adminAccess && (
                  <Button
                    size="sm"
                    className="bg-[#da402f] dark:bg-[#7375E1] text-white hover:bg-[#c73a2a] dark:hover:bg-[#6b5fd1]"
                    startContent={<FiPlus size={16} />}
                    onClick={() => setIsAddProjectModalOpen(true)}
                  >
                    Proje Ekle
                  </Button>
                )}
              </div>
              
              {projects.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {projects.map((project) => (
                    <Card key={project.project_id} className="bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-none">
                      <CardBody className="p-4">
                        <div className="flex items-center justify-between mb-3">
                          <h4 className="font-semibold text-gray-900 dark:text-white">
                            {project.pname}
                          </h4>
                          <Dropdown>
                            <DropdownTrigger>
                              <Button isIconOnly variant="light" size="sm">
                                <FiMoreVertical size={16} />
                              </Button>
                            </DropdownTrigger>
                            <DropdownMenu>
                              <DropdownItem 
                                key="remove" 
                                startContent={<FiTrash2 size={16} />}
                                className="text-red-600"
                                onClick={() => handleRemoveProject(project.project_id, project.pname)}
                              >
                                Çıkar
                              </DropdownItem>
                            </DropdownMenu>
                          </Dropdown>
                        </div>
                        <div className="space-y-2">
                          <div className="flex justify-between text-sm">
                            <span className="text-gray-600 dark:text-gray-400">Görevler:</span>
                            <span className="font-medium">{project.total_tasks}</span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span className="text-gray-600 dark:text-gray-400">Tamamlanan:</span>
                            <span className="font-medium text-green-600">{project.completed_tasks}</span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span className="text-gray-600 dark:text-gray-400">Tamamlanma:</span>
                            <span className="font-medium">{project.completion_rate}%</span>
                          </div>
                        </div>
                      </CardBody>
                    </Card>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <p className="text-gray-600 dark:text-gray-400 mb-4">
                    Bu organizasyonda henüz proje yok
                  </p>
                  {adminAccess && (
                    <Button
                      size="sm"
                      className="bg-[#da402f] dark:bg-[#7375E1] text-white hover:bg-[#c73a2a] dark:hover:bg-[#6b5fd1]"
                      startContent={<FiPlus size={16} />}
                      onClick={() => setIsAddProjectModalOpen(true)}
                    >
                      İlk Projeyi Ekle
                    </Button>
                  )}
                </div>
              )}
            </CardBody>
          </Card>
        )}

        {activeTab === 'users' && (
          <Card className="bg-white dark:bg-dark-300 border border-gray-200 dark:border-gray-700 shadow-none">
            <CardBody className="p-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Kullanıcılar
                </h3>
                {adminAccess && (
                  <Button
                    size="sm"
                    className="bg-[#da402f] dark:bg-[#7375E1] text-white hover:bg-[#c73a2a] dark:hover:bg-[#6b5fd1]"
                    startContent={<FiPlus size={16} />}
                    onClick={() => setIsAddUserModalOpen(true)}
                  >
                    Kullanıcı Ekle
                  </Button>
                )}
              </div>
              
              {users.length > 0 ? (
                <Table aria-label="Kullanıcılar tablosu">
                  <TableHeader>
                    <TableColumn>Kullanıcı</TableColumn>
                    <TableColumn>Görevler</TableColumn>
                    <TableColumn>Tamamlanan</TableColumn>
                    <TableColumn>Performans</TableColumn>
                    <TableColumn>İşlemler</TableColumn>
                  </TableHeader>
                  <TableBody>
                    {users.map((user) => (
                      <TableRow key={user.id}>
                        <TableCell>
                          <div className="flex items-center">
                            <Avatar
                              src={user.uimg}
                              name={`${user.ufname} ${user.ulname}`}
                              size="sm"
                              className="mr-3"
                            />
                            <div>
                              <p className="font-medium text-gray-900 dark:text-white">
                                {user.ufname} {user.ulname}
                              </p>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>{user.total_tasks}</TableCell>
                        <TableCell className="text-green-600">{user.completed_tasks}</TableCell>
                        <TableCell>{user.completion_rate}%</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            {adminAccess && (
                              <Button
                                size="sm"
                                variant="light"
                                onClick={() => openRoleModal(user)}
                              >
                                Rol
                              </Button>
                            )}
                            {adminAccess && (
                              <Button
                                size="sm"
                                color="danger"
                                variant="light"
                                startContent={<FiTrash2 size={16} />}
                                onClick={() => handleRemoveUser(user.id, `${user.ufname} ${user.ulname}`)}
                              >
                                Çıkar
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <div className="text-center py-8">
                  <p className="text-gray-600 dark:text-gray-400 mb-4">
                    Bu organizasyonda henüz kullanıcı yok
                  </p>
                  {adminAccess && (
                    <Button
                      size="sm"
                      className="bg-[#da402f] dark:bg-[#7375E1] text-white hover:bg-[#c73a2a] dark:hover:bg-[#6b5fd1]"
                      startContent={<FiPlus size={16} />}
                      onClick={() => setIsAddUserModalOpen(true)}
                    >
                      İlk Kullanıcıyı Ekle
                    </Button>
                  )}
                </div>
              )}
            </CardBody>
          </Card>
        )}

        {/* Kullanıcı Rol Değiştirme Modal */}
        <Modal isOpen={isRoleModalOpen} onClose={() => setIsRoleModalOpen(false)}>
          <ModalContent>
            <ModalHeader>
              <h2 className="text-xl font-semibold">Kullanıcı Rolünü Değiştir</h2>
            </ModalHeader>
            <ModalBody>
              <div className="space-y-4">
                <Select
                  label="Proje"
                  placeholder="Proje seçin"
                  selectedKeys={selectedProjectSlug ? [selectedProjectSlug] : []}
                  onChange={(e) => setSelectedProjectSlug(e.target.value)}
                >
                  {projects.map((p) => (
                    <SelectItem key={p.pslug} value={p.pslug}>
                      {p.pname}
                    </SelectItem>
                  ))}
                </Select>
                <Select
                  label="Rol"
                  placeholder="Rol seçin"
                  selectedKeys={[selectedRole]}
                  onChange={(e) => setSelectedRole(e.target.value)}
                >
                  <SelectItem key="2" value="2">Proje Sahibi (Owner)</SelectItem>
                  <SelectItem key="3" value="3">Moderatör (Moderator)</SelectItem>
                  <SelectItem key="1" value="1">Kullanıcı (Member)</SelectItem>
                </Select>
              </div>
            </ModalBody>
            <ModalFooter>
              <Button variant="light" onPress={() => setIsRoleModalOpen(false)}>İptal</Button>
              <Button className="bg-[#da402f] dark:bg-[#7375E1] text-white" onPress={handleChangeUserRole}>Kaydet</Button>
            </ModalFooter>
          </ModalContent>
        </Modal>

        {activeTab === 'reminders' && (
          <Card className="bg-white dark:bg-dark-300 border border-gray-200 dark:border-gray-700 shadow-none">
            <CardBody className="p-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Hatırlatmalar
                </h3>
                <Chip color="primary" variant="flat" size="sm">
                  {reminders.length} Hatırlatma
                </Chip>
              </div>
              
              {reminders.length > 0 ? (
                <div className="space-y-3">
                  {reminders.map((reminder, index) => (
                    <div key={index} className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
                      <div className="flex items-center">
                        <div className="p-2 bg-orange-100 dark:bg-orange-900/30 rounded-lg mr-3">
                          <FiBell className="text-orange-600 dark:text-orange-400" size={20} />
                        </div>
                        <div>
                          <div className="flex items-center space-x-2 mb-1">
                            <p className="font-medium text-gray-900 dark:text-white">
                              {reminder.reminder_name}
                            </p>
                            <Chip 
                              size="sm" 
                              variant="flat"
                              className="bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300"
                            >
                              {reminder.type === 'domain' ? 'Domain' : 
                               reminder.type === 'hosting' ? 'Hosting' : 
                               reminder.type === 'ssl' ? 'SSL' : 
                               reminder.type === 'backup' ? 'Yedekleme' : 
                               reminder.type === 'other' ? 'Diğer' : reminder.type}
                            </Chip>
                          </div>
                          <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">
                            {reminder.description}
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-500">
                            Proje: {reminder.projectName}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-medium text-gray-900 dark:text-white">
                          {new Date(reminder.end_date).toLocaleDateString('tr-TR')}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <div className="p-4 bg-gray-100 dark:bg-gray-800 rounded-full w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                    <FiBell className="text-gray-400" size={24} />
                  </div>
                  <p className="text-gray-600 dark:text-gray-400 mb-4">
                    Henüz hatırlatma yok
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-500">
                    Projelerde hatırlatma oluşturduğunuzda burada görünecek
                  </p>
                </div>
              )}
            </CardBody>
          </Card>
        )}


        {/* Add User Modal */}
        <Modal isOpen={isAddUserModalOpen} onClose={() => setIsAddUserModalOpen(false)}>
          <ModalContent>
            <ModalHeader>
              <h2 className="text-xl font-semibold">Kullanıcı Ekle</h2>
            </ModalHeader>
            <ModalBody>
              <Input
                label="Kullanıcı ID"
                placeholder="Kullanıcı ID'sini girin"
                value={newUserId}
                onChange={(e) => setNewUserId(e.target.value)}
                type="number"
              />
            </ModalBody>
            <ModalFooter>
              <Button variant="light" onPress={() => setIsAddUserModalOpen(false)}>
                İptal
              </Button>
              <Button 
                className="bg-[#da402f] dark:bg-[#7375E1] text-white hover:bg-[#c73a2a] dark:hover:bg-[#6b5fd1]"
                onPress={handleAddUser}
              >
                Ekle
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>

        {/* Add Project Modal */}
        <Modal isOpen={isAddProjectModalOpen} onClose={() => setIsAddProjectModalOpen(false)}>
          <ModalContent>
            <ModalHeader>
              <h2 className="text-xl font-semibold">Proje Ekle</h2>
            </ModalHeader>
            <ModalBody>
              <Input
                label="Proje ID"
                placeholder="Proje ID'sini girin"
                value={newProjectId}
                onChange={(e) => setNewProjectId(e.target.value)}
                type="number"
              />
            </ModalBody>
            <ModalFooter>
              <Button variant="light" onPress={() => setIsAddProjectModalOpen(false)}>
                İptal
              </Button>
              <Button 
                className="bg-[#da402f] dark:bg-[#7375E1] text-white hover:bg-[#c73a2a] dark:hover:bg-[#6b5fd1]"
                onPress={handleAddProject}
              >
                Ekle
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      </div>
    </div>
  );
};

export default OrganizationDetail;
